// GPU renderer orchestrator (Fase 2).

import { Container } from 'pixi.js';
import type { Camera } from '../camera';
import { getPaletteFilter, syncPaletteFilter } from './palette-filter';
import { getTilemapLayer, drawTilemapGpu, clearTilemapGpu } from './tilemap';
import { getSpriteLayer, clearSpriteLayer } from './sprites';
import { getBgLayer, getFxLayer, clearFxGpu, clearBgLayerGpu } from './procedural';
import { profilerMeasure } from '../../dev/profiler';

let worldRoot: Container | null = null;
let indexedRoot: Container | null = null;
let currentCam: Camera | null = null;

export function initGpuRenderer(stage: Container): Container {
  if (worldRoot) return worldRoot;

  worldRoot = new Container();
  indexedRoot = new Container();
  indexedRoot.filters = [getPaletteFilter()];
  indexedRoot.addChild(getTilemapLayer());
  indexedRoot.addChild(getSpriteLayer());

  worldRoot.addChild(getBgLayer());
  worldRoot.addChild(indexedRoot);
  worldRoot.addChild(getFxLayer());
  stage.addChildAt(worldRoot, 0);
  return worldRoot;
}

export function getGpuWorldRoot(): Container | null {
  return worldRoot;
}

export function setGpuCamera(cam: Camera): void {
  currentCam = cam;
  if (!worldRoot) return;
  const scale = cam.pixelScale;
  worldRoot.scale.set(scale);
  worldRoot.position.set(-cam.coverCropX, -cam.coverCropY);
}

export function getActiveGpuCamera(): Camera | null {
  return currentCam;
}

export function beginGpuFrame(cam: Camera): void {
  profilerMeasure('gpu.begin', () => {
    currentCam = cam;
    syncPaletteFilter();
    clearBgLayerGpu(cam);
    clearTilemapGpu();
    clearSpriteLayer();
    clearFxGpu();
  });
}

export function endGpuFrame(): void {
  profilerMeasure('gpu.end', () => {
    syncPaletteFilter();
  });
}

export function destroyGpuRenderer(): void {
  if (worldRoot?.parent) worldRoot.parent.removeChild(worldRoot);
  worldRoot?.destroy({ children: true });
  worldRoot = null;
  indexedRoot = null;
  currentCam = null;
}

export { syncPaletteFilter } from './palette-filter';

export { drawTilemapGpu } from './tilemap';
export {
  drawPlayerGpu,
  drawWorldBlitGpu,
  drawIndexedSpriteWorld,
  clearSpriteLayer,
} from './sprites';
export {
  drawShieldGpu,
  drawCircuitArcsGpu,
  drawCircuitBarGpu,
  drawCircleGpu,
  drawCircleExGpu,
  drawWorldCircleGpu,
  drawArcGpu,
  drawFilledRectGpu,
  clearFxGpu,
} from './procedural';
